import {
  DetailsRow,
  GroupHeader,
  IColumn,
  Icon,
  IconButton,
  IDetailsListStyles,
  IDetailsRowProps,
  IGroup,
  IGroupHeaderProps,
  IRenderFunction,
  Shimmer,
  ShimmerElementType,
  TooltipHost,
} from '@fluentui/react';
import { ClassNamesFromIStyles, DetailsList, Info, Search, useClassNames, useTheme } from '@h2oai/ui-kit';
import React, { useCallback, useMemo, useState } from 'react';

import {
  customTableStyles,
  dataTableStylesDefault,
  groupIconStyle,
  iconStyles,
  IDataTableStyles,
  infoStyles,
  searchStyles,
  tooltipHostStyles,
} from './DataTable.styles';
import { buildHierarchicalGroups, buildHierarchicalGroupsWithCustomTitles } from './DataTable.utils';

export interface IDataTableColumn extends IColumn {
  headerTooltip?: string;
}

export interface ICustomGroupConfig {
  fieldName: string;
  titleFormatter: (groupValue: string, groupItems: any[], parentKey?: string) => string;
  level?: number;
  isCollapsed?: boolean;
  styleParentheses?: boolean;
}

interface IDataTableProps {
  items: any[];
  columns: IDataTableColumn[];
  searchPlaceholder?: string;
  searchFields: string[];
  groups?: IGroup[];
  styles?: Partial<IDetailsListStyles>;
  contentHeight?: string | number;
  loading?: boolean;
  error?: boolean;
  loadingRowCount?: number;
  groupableFields?: string[];
  sortableFields?: string[];
  defaultGroupedColumns?: string[];
  customGroupConfig?: ICustomGroupConfig[];
}

const DataTable = ({
  items,
  columns,
  searchPlaceholder,
  searchFields,
  groups: initialGroups,
  styles: userStyles,
  contentHeight,
  loading = false,
  error = false,
  loadingRowCount = 5,
  groupableFields = [],
  sortableFields = [],
  defaultGroupedColumns = [],
  customGroupConfig = [],
}: IDataTableProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [groupByKeys, setGroupByKeys] = useState<string[]>(defaultGroupedColumns);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [isSortedDescending, setIsSortedDescending] = useState<boolean>(false);

  const theme = useTheme();
  const classNames = useClassNames<IDataTableStyles, ClassNamesFromIStyles<IDataTableStyles>>(
    'data-table',
    dataTableStylesDefault(theme)
  );

  const skeletonItems = useMemo(() => {
    if (!loading) return [];

    return Array(loadingRowCount)
      .fill(null)
      .map((_, index) => ({
        key: `skeleton-${index}`,
        isSkeletonRow: true,
      }));
  }, [loading, loadingRowCount]);

  const filteredData = useMemo(() => {
    if (loading) return skeletonItems;
    if (!searchFields.length) return items;
    const search = searchTerm.toLowerCase();
    const validFields = columns
      .map((col) => col.fieldName)
      .filter((field): field is string => field !== undefined && searchFields.includes(field));

    return items.filter((item) =>
      validFields.some((field) => typeof item[field] === 'string' && item[field].toLowerCase().includes(search))
    );
  }, [items, searchTerm, columns, searchFields, loading]);

  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;

    return [...filteredData].sort((a, b) => {
      const valA = a[sortColumn];
      const valB = b[sortColumn];

      if (valA == null && valB == null) return 0;
      if (valA == null) return isSortedDescending ? 1 : -1;
      if (valB == null) return isSortedDescending ? -1 : 1;

      if (typeof valA === 'string' && typeof valB === 'string') {
        const comparison = valA.localeCompare(valB);
        return isSortedDescending ? -comparison : comparison;
      }

      if (typeof valA === 'number' && typeof valB === 'number') {
        return isSortedDescending ? valB - valA : valA - valB;
      }

      if (valA instanceof Date && valB instanceof Date) {
        const comparison = valA.getTime() - valB.getTime();
        return isSortedDescending ? -comparison : comparison;
      }

      if (valA < valB) return isSortedDescending ? 1 : -1;
      if (valA > valB) return isSortedDescending ? -1 : 1;
      return 0;
    });
  }, [filteredData, sortColumn, isSortedDescending]);

  const { groupedItems, groups } = useMemo(() => {
    if (loading) {
      return { groupedItems: skeletonItems, groups: undefined };
    }
    if (sortedData.length === 0) {
      return { groupedItems: [], groups: undefined };
    }

    if (customGroupConfig.length > 0) {
      return buildHierarchicalGroupsWithCustomTitles(sortedData, customGroupConfig);
    }

    if (groupByKeys.length === 0) {
      return { groupedItems: sortedData, groups: initialGroups };
    }

    return buildHierarchicalGroups(sortedData, groupByKeys, columns);
  }, [loading, skeletonItems, sortedData, groupByKeys, initialGroups, columns, customGroupConfig]);

  const isEmpty = !loading && groupedItems.length === 0;
  const displayData = error ? [{ key: '__error__' }] : isEmpty ? [{ key: '__empty__' }] : groupedItems;

  const getShimmerElements = useMemo(() => {
    return columns.map((column) => {
      const width = column.calculatedWidth || column.maxWidth || column.minWidth || 100;

      return {
        type: ShimmerElementType.line,
        width: width - 16,
        height: 16,
      };
    });
  }, [columns]);

  const handleColumnClick = useCallback(
    (_ev: React.MouseEvent, column: IColumn) => {
      const fieldName = column.fieldName;
      if (!fieldName) return;

      const isSortable = sortableFields.length === 0 || sortableFields.includes(fieldName);
      if (!isSortable) return;

      if (sortColumn === fieldName) {
        setIsSortedDescending(!isSortedDescending);
      } else {
        setSortColumn(fieldName);
        setIsSortedDescending(false);
      }
    },
    [sortColumn, isSortedDescending, sortableFields]
  );

  const renderHeader = useCallback(
    (colName: string, colKey: string, headerTooltip?: string) => {
      const isGroupable = groupableFields.includes(colKey);
      const isGrouped = groupByKeys.includes(colKey);
      const isSortable = sortableFields.length === 0 || sortableFields.includes(colKey);
      const isDefaultGrouped = defaultGroupedColumns.includes(colKey);

      if (!isGroupable && !isSortable && !headerTooltip) {
        return <>{colName}</>;
      }

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>{colName}</span>

          {headerTooltip && (
            <Info styles={infoStyles} isTooltip>
              {headerTooltip}
            </Info>
          )}

          {isGroupable && (
            <TooltipHost
              content={isGrouped ? `Remove grouping by ${colName}` : `Group by ${colName}`}
              styles={tooltipHostStyles}
            >
              <IconButton
                iconProps={{ iconName: 'GroupedList' }}
                styles={groupIconStyle(isGrouped, theme)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isDefaultGrouped) {
                    return;
                  }
                  setGroupByKeys((prev) => {
                    if (prev.includes(colKey)) {
                      return prev.filter((k) => k !== colKey);
                    }
                    return prev.length < 3 ? [...prev, colKey] : prev;
                  });
                }}
                disabled={isDefaultGrouped}
              />
            </TooltipHost>
          )}
        </div>
      );
    },
    [groupableFields, groupByKeys, sortableFields, defaultGroupedColumns, theme]
  );

  const enhancedColumns = useMemo(() => {
    return columns.map((column) => {
      const fieldName = column.fieldName || '';
      const isSortable = sortableFields.length === 0 || sortableFields.includes(fieldName);

      return {
        ...column,
        onRenderHeader: () => renderHeader(column.name, fieldName, column.headerTooltip),
        showSortIconWhenUnsorted: isSortable,
        isSorted: sortColumn === fieldName,
        isSortedDescending: sortColumn === fieldName && isSortedDescending,
        onColumnClick: isSortable ? handleColumnClick : undefined,
        styles: {
          ...column.styles,
          root: {
            cursor: isSortable ? 'pointer' : 'default',
          },
        },
      };
    });
  }, [columns, sortColumn, isSortedDescending, groupByKeys, theme, renderHeader, handleColumnClick, sortableFields]);

  const onRenderRow: IRenderFunction<IDetailsRowProps> = (props) => {
    if (!props) return null;

    // eslint-disable-next-line react/prop-types
    if (props?.item?.key === '__empty__') {
      return (
        <div className={classNames.emptyRowContainer}>
          <div className={classNames.emptyRowContentWrapper}>
            <div className={classNames.emptyRowIconWrapper}>
              <Icon iconName="SearchData" styles={iconStyles(theme)} />
            </div>
            <div>No results found.</div>
          </div>
        </div>
      );
    }

    // eslint-disable-next-line react/prop-types
    if (props?.item?.isSkeletonRow) {
      return (
        <div className={classNames.skeletonRow}>
          <Shimmer shimmerElements={getShimmerElements} width="100%" style={{ padding: '8px 12px' }} />
        </div>
      );
    }

    // eslint-disable-next-line react/prop-types
    if (props?.item?.key === '__error__') {
      return (
        <div className={classNames.emptyRowContainer}>
          <div className={classNames.emptyRowContentWrapper}>
            <div className={classNames.emptyRowIconWrapper}>
              <Icon iconName="Error" styles={iconStyles(theme)} />
            </div>
            <div>An error occured.</div>
          </div>
        </div>
      );
    }

    return <DetailsRow {...props} />;
  };

  const mergedStyles = useMemo(() => {
    const baseStyles = customTableStyles(theme, contentHeight, isEmpty);

    if (!userStyles) return baseStyles;

    const mergedRootSelectors = {
      ...(baseStyles.root as any)?.selectors,
      ...(userStyles.root as any)?.selectors,
    };

    return {
      ...baseStyles,
      ...userStyles,
      root: {
        ...(baseStyles.root as object),
        ...(userStyles.root as object),
        ...(Object.keys(mergedRootSelectors).length > 0
          ? {
              selectors: mergedRootSelectors,
            }
          : {}),
      },
    };
  }, [theme, isEmpty, userStyles]);

  const onRenderGroupHeader = useCallback(
    (props?: IGroupHeaderProps) => {
      if (!props) return null;

      const groupName = props.group?.name || '';
      const groupLevel = props.group?.level ?? 0;

      const config = customGroupConfig[groupLevel];
      const shouldStyleParentheses = config?.styleParentheses ?? false;

      const match = groupName.match(/^(.*?)(\(.*?\))(.*)$/);

      const onRenderTitle = (titleProps?: IGroupHeaderProps) => {
        if (!titleProps || !shouldStyleParentheses || !match) {
          return <span style={{ fontSize: 16, fontWeight: 'normal' }}>{groupName}</span>;
        }

        const [, before, parenSection, after] = match;
        return (
          <span>
            <span style={{ fontSize: 16, fontWeight: 'normal' }}>{before}</span>
            <span style={{ fontSize: '0.85em', fontWeight: 'normal' }}>{parenSection}</span>
            <span style={{ fontSize: 16, fontWeight: 'normal' }}>{after}</span>
          </span>
        );
      };

      return <GroupHeader {...props} onRenderTitle={onRenderTitle} />;
    },
    [customGroupConfig]
  );

  return (
    <>
      {searchFields.length ? (
        <Search
          emptyMessage="No results found"
          placeholder={searchPlaceholder}
          onSearchTextChange={(text: string) => setSearchTerm(text)}
          styles={searchStyles()}
        />
      ) : (
        <></>
      )}
      <div className={classNames.tableContainer}>
        <DetailsList
          items={displayData}
          columns={enhancedColumns}
          styles={mergedStyles}
          onRenderRow={onRenderRow}
          groups={groups}
          groupProps={{
            showEmptyGroups: true,
            onRenderHeader: customGroupConfig.length > 0 ? onRenderGroupHeader : undefined,
          }}
        />
      </div>
    </>
  );
};

export default DataTable;
